import { motion } from "framer-motion";
import { ArrowDownRight, Phone } from "lucide-react";
import { COMPANY } from "@/data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 + i * 0.12 },
  }),
};

export default function Hero() {
  const scrollTo = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      data-testid="hero"
      className="relative min-h-screen bg-[#F5F4EF] text-black overflow-hidden"
    >
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 pt-32 pb-16 min-h-screen grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex items-center gap-4 font-mono-accent text-xs uppercase tracking-[0.25em] mb-10"
          >
            <span className="h-2 w-2 bg-signal-yellow animate-pulse" />
            <span className="bg-signal-yellow text-black px-2 py-1">Live 24×7</span>
            <span className="text-black/50">/ Mumbai · Mira Bhayandar</span>
          </motion.div>

          <h1 className="font-display font-medium leading-[0.9] tracking-tighter text-[14vw] lg:text-[7.5vw]">
            {["READY-MIX", "CONCRETE,", "ON DEMAND."].map((t, i) => (
              <span key={t} className="block overflow-hidden">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                  {i === 2 ? (
                    <>
                      ON{" "}
                      <span className="bg-signal-yellow text-black px-3 inline-block leading-none">
                        DEMAND.
                      </span>
                    </>
                  ) : (
                    t
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-10 flex flex-col md:flex-row md:items-end gap-8"
          >
            <p className="max-w-md text-base md:text-lg text-black/70 leading-relaxed">
              {COMPANY.tagline} From foundations to highways, railways to high-rises —
              batched to spec and delivered fresh across the city.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                data-testid="hero-quote-btn"
                onClick={() => scrollTo("#quote")}
                className="group flex items-center gap-3 bg-black text-[#F5F4EF] px-8 py-5 font-mono-accent text-xs uppercase tracking-[0.2em] hover:bg-signal-yellow hover:text-black transition-colors"
              >
                Request a Quote
                <ArrowDownRight size={18} className="group-hover:rotate-45 transition-transform" />
              </button>
              <a
                data-testid="hero-call-btn"
                href={`tel:${COMPANY.phoneRaw}`}
                className="flex items-center gap-3 border border-black/30 px-8 py-5 font-mono-accent text-xs uppercase tracking-[0.2em] hover:border-black hover:bg-black hover:text-[#F5F4EF] transition-colors"
              >
                <Phone size={16} /> Call Now
              </a>
            </div>
          </motion.div>
        </div>

        {/* Right: clipped spotlight image */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative border border-black/15 overflow-hidden aspect-[4/5]">
            <img
              src="https://images.pexels.com/photos/12032964/pexels-photo-12032964.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=940"
              alt="Ready-mix concrete batching plant"
              className="h-full w-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-signal-yellow px-5 py-4 flex items-center justify-between">
              <span className="font-mono-accent text-xs uppercase tracking-[0.2em] text-black">Fresh Delivery</span>
              <span className="font-display text-2xl text-black">M10–M70</span>
            </div>
          </div>
          <div className="absolute -top-4 -left-4 h-16 w-16 bg-black hidden lg:block" />
        </motion.div>
      </div>
    </section>
  );
}
